import React, {useEffect, useMemo, useState} from "react";
import {Badge, Button, Checkbox, Col, Row, Space, Tooltip} from "antd";
import {
    AddIcon,
    DiamondIcon,
    DollarIcon,
    FlameIcon,
    StorageBoxOutlineIcon,
    WishlistIcon
} from "@components/icons/Icons";
import BookCover from "@components/book-cover";
import {formatGrade} from "@lib/format";
import {useWishlistRemoveIssueMutation} from "@mwServices/User";
import {showResponse} from "@services/Api";
import {IssueRenderProps} from "@components/grid/IssueGrid";
import useIsLoggedIn from "@hooks/useIsLoggedIn";
import AddComicModal from "@components/add-comic-modal";
import moment from "moment";
import {FmvPrice} from "@components/fmv-price";
import {KeyIssueBadge} from "@components/key-issue-badge";


export interface WishlistGridItemProps extends IssueRenderProps {
    showEditMultiple?: boolean;
    isSelected?: boolean;
    toggleSelected: (collectionId: number) => void;
    searchCollection?: (newQuery: string) => void;
    showDesktopView?: boolean;
    showValueRibbon?: boolean;
    windowDimensions?: any;
}

const WishlistGridItem = ({
                              item,
                              onIssueUpdate,
                              showEditMultiple,
                              isSelected,
                              toggleSelected,
                              searchCollection,
                              showValueRibbon,
                              showDesktopView,
                              windowDimensions
}: WishlistGridItemProps) => {
    const isLoggedIn = useIsLoggedIn();
    const [wishlistIssueRemove, removeWishlistResponse] = useWishlistRemoveIssueMutation();
    const [addIssueId, setAddIssueId] = useState<number | null>(null);
    const issueDate = useMemo(() => { return moment(item.date.split(' ').shift()).toDate(); }, [item]);

    useEffect(() => {
        if (removeWishlistResponse.isSuccess && onIssueUpdate) onIssueUpdate({ ...item, in_wishlist: false });
        showResponse(removeWishlistResponse, 'Issue removed from wishlist.', 'Failed to remove issue from wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeWishlistResponse]);

    return (
        <>
            <div className="covr-grid-header">
                <Row justify="space-between">
                    <span className="min-height-24">
                        {showEditMultiple && (
                            <Checkbox
                                checked={isSelected}
                                onChange={() => toggleSelected(item.id)}
                            />
                        )}
                    </span>
                    {item.total_in_collection > 0 && (
                        <a onClick={() => {
                            if (searchCollection) searchCollection(item.series_title + ' ' + item.issue_number)
                        }}>
                            <span className="issues-owned">
                                <StorageBoxOutlineIcon/><strong>&nbsp;:&nbsp;<span
                                className="total-number">{item.total_in_collection}</span></strong>
                            </span>
                        </a>
                    )}
                </Row>
            </div>
            <div className="covr-grid-content">
                <a href={`/issue/${item.slug}`}>
                    <BookCover issue={item} preview={false} text={item.variant_name}/>
                </a>
            </div>
            <div className="covr-grid-footer">
                <Row justify="center">
                    <Col span={24}>
                    <Row className="issue-grid-book-cover-value-white"
                             style={{borderBottomWidth: showValueRibbon ? 0 : 1}}>
                            <Col span={12}>
                                <span className="issueNumber">{`${item.issue_number}`}</span>
                                <span className="issueYear">({issueDate.getFullYear()})</span>
                            </Col>
                            <Col span={12} className="valueBadges">
                                {item.is_key && (
                                    <KeyIssueBadge keyInfo={item.key_info} windowDimensions={windowDimensions}
                                                   isLargeScreen={showDesktopView}/>)}
                                {item.is_hot && (<div className="valueBadge hotIssue isActive"><FlameIcon/></div>)}
                                {item.is_rare && (<div className="valueBadge rareIssue isActive"><DiamondIcon/></div>)}
                            </Col>
                        </Row>

                        {showValueRibbon && (
                            <Row
                                className={'issue-grid-book-cover-value ' + ((item.graded_by && item.graded_by.toUpperCase() !== 'RAW') ? 'blue ' : 'grey ')}>
                                <Col span={24}>
                                    <span className="issueGrade">
                                        {item.graded_by ? item.graded_by.toUpperCase() : item.graded_or_raw.toUpperCase()} {formatGrade(item.grade)} |
                                    </span>
                                    <FmvPrice
                                        prefix={
                                            (item.is_most_common_value && item.most_common_grade) ?
                                                ('(' + formatGrade(item.most_common_grade) + ')') :
                                                null
                                        }
                                        value={item.value}
                                        stale={item.stale}
                                    />
                                </Col>
                            </Row>
                        )}

                        <Row className="issue-grid-book-cover-details">
                            <p className="title" title={item.series_title}>
                                {item.series_title}
                            </p>
                        </Row>
                        {isLoggedIn && (
                            <Row className="issue-actions" justify="center">
                                <Space>
                                    <Tooltip title="Remove from Wishlist">
                                        <Button className={"issueButton greyButton "} shape="circle" onClick={() => {
                                            wishlistIssueRemove([item.id]);
                                        }}>
                                            <WishlistIcon/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Add to Collection">
                                        <Button className="issueButton greyButton" shape="circle" size="large"
                                                onClick={() => {
                                                    setAddIssueId(item.id);
                                                }}>
                                            <AddIcon/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Buy in Marketplace">
                                        <a href={`/marketplace/?issue_id=${item.id}`}>
                                            <Button className="issueButton greyButton" shape="circle">
                                                <DollarIcon className="dollar"/>
                                            </Button>
                                        </a>
                                    </Tooltip>
                                </Space>
                            </Row>
                        )}
                    </Col>
                </Row>
                <div className="select-arrow"/>
            </div>

            {addIssueId && (
                <AddComicModal isModalOpen={true} issueIds={[addIssueId]} onCancel={() => setAddIssueId(null)}
                               onIssueUpdate={onIssueUpdate}/>
            )}
        </>
    );
};

export default WishlistGridItem;